import { isProduction } from "@ctv/core/config/env";
import { datadogRum } from "@datadog/browser-rum";
import { BaseLogClientErrorContext } from "./types";

/**
 * DatadogClientError is a type that represents the structure of an error object
 * that can be logged to Datadog RUM (Real User Monitoring).
 * It includes the error itself, an optional message, and additional context.
 */
export type DatadogClientError = {
  error: unknown;
  message?: string;
  context: BaseLogClientErrorContext;
};

/**
 * Logs an error to Datadog RUM and the console.
 * This function is used to capture client-side errors and send them to Datadog for monitoring.
 * It ensures that the error is an instance of Error, and if not, it creates a new Error with the provided message.
 * The context parameter allows for additional information to be sent along with the error.
 * @param {DatadogClientError} params - The parameters for logging the error.
 * @param {unknown} params.error - The error object to log.
 * @param {string} [params.message] - An optional message to include with the error.
 * @param {BaseLogClientErrorContext} params.context - Additional context to include with the error.
 * @returns {void}
 */
export function logClientError({
  error,
  message,
  context,
}: DatadogClientError): void {
  const errorInstance =
    error instanceof Error
      ? error
      : new Error(
          typeof message === "string"
            ? message
            : typeof error === "string"
            ? error
            : "Unknown error"
        );

  if (datadogRum?.addError) {
    datadogRum.addError(errorInstance, context);
  }
  // Log the error to the console for debugging purposes
  if (!isProduction) {
    console.error(errorInstance, context);
  }
}
